import { useDispatch, useSelector } from 'react-redux';
import { setFormField, setFieldError, fetchInitialState } from './managerDetailsSlice';
import FloatingLabelInput from "../../components/floatingLabelInput";
import FloatingLabelSelect from "../../components/floatingLabelSelect";
import { statesList } from '../../config/constant';

import { formatPhoneNumber, validateField } from '../utils';
import { useEffect } from 'react';
import Loader from '../../components/loader';
import { StepHeading, StepShell } from '../../components/optionCard';
import { fetchOnboardingForm } from '../2_EmployeeDetails/employeeDetailsSlice';
import { BDS_TEAM_LEAD_TITLE, isRizeForRealtorFlow } from '../../config/flows';



const ManagerDetails = () => {
    const dispatch = useDispatch();
    const managerDetailsState = useSelector(state => state.manager)
    const onboardingForm = useSelector(state => state.employees.onboardingForm);
    const linkMeta = useSelector(state => state.navigator.linkMeta);

    // Rize for Realtor team: this section is the Business Development Team Lead Details
    // (City / State stay, NMLS # is not asked)
    const isRizeTeam = isRizeForRealtorFlow(linkMeta?.newType);

    // The only title for this flow, once it exists in the title dropdown data
    const forcedTitle = (isRizeTeam && onboardingForm.some(item => item.Title === BDS_TEAM_LEAD_TITLE))
        ? BDS_TEAM_LEAD_TITLE
        : null;
    const titleOptions = forcedTitle
        ? onboardingForm.filter(item => item.Title === forcedTitle)
        : onboardingForm;


    useEffect(() => {
        dispatch(fetchInitialState());
        if (onboardingForm.length === 0) {
            dispatch(fetchOnboardingForm());
        }
    },[dispatch, onboardingForm.length])

    useEffect(() => {
        if (forcedTitle && managerDetailsState.data.title !== forcedTitle) {
            dispatch(setFormField({ field: 'title', value: forcedTitle }));
        }
    }, [forcedTitle, managerDetailsState.data.title, dispatch])


    const handleChange = (e) => {
        const { name, value } = e.target;
        const validation = validateField(name, value);

        dispatch(setFormField({ field: name, value }));

        if (name === "cellNumber" || name === "officeNumber") {
            const formattedPhoneNumber = formatPhoneNumber(value);
            dispatch(setFormField({ field: name, value: formattedPhoneNumber }));
            dispatch(setFieldError({ field: name, error: "" }));
        } else if (name === "nmls") {
            // Only validate NMLS if "isOriginatingLoans"
            if (managerDetailsState.data.isOriginatingLoans) {
                if (!validation) {
                    dispatch(
                        setFieldError({
                            field: name,
                            error: `Please enter a valid ${name}`,
                        })
                    );
                } else {
                    dispatch(setFieldError({ field: name, error: "" }));
                }
            } else {
                // Ensure no error for NMLS when title is not "Producing Branch Manager"
                dispatch(setFieldError({ field: name, error: "" }));
            }
        } else {
            if (!validation) {
                dispatch(
                    setFieldError({ field: name, error: `Please enter a valid ${name}` })
                );
            } else {
                dispatch(setFieldError({ field: name, error: "" }));
            }
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;

        if (name === "nmls") {
            // Only validate NMLS if "isOriginatingLoans"
            if (managerDetailsState.data.isOriginatingLoans && !value) {
                dispatch(setFieldError({ field: "nmls", error: "NMLS is required" }));
            } else {
                dispatch(setFieldError({ field: "nmls", error: "" })); // Clear error if valid
            }
        } else if (name === "cellNumber" || name === "officeNumber") {
            const rawNumbers = value.replace(/[^\d]/g, "");
            if (rawNumbers && rawNumbers.length !== 10) {
                dispatch(
                    setFieldError({
                        field: name,
                        error: "Please enter a valid phone number",
                    })
                );
            } else {
                dispatch(setFieldError({ field: name, error: "" })); // Clear error if valid
            }
        } else if (name === "email") {
            const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (value && !emailPattern.test(value)) {
                dispatch(
                    setFieldError({
                        field: name,
                        error: "Please enter a valid email address",
                    })
                );
            } else {
                dispatch(setFieldError({ field: name, error: "" })); // Clear error if valid
            }
        } else if (name !== "nmls") {
            // Exclude 'nmls' from general validation
            // General validation for other fields: show error if field is empty
            if (!value) {
                dispatch(
                    setFieldError({ field: name, error: `Please enter a valid ${name}` })
                );
            } else {
                dispatch(setFieldError({ field: name, error: "" })); // Clear error if valid
            }
        }
    };
    

    return (
        <>
            {managerDetailsState.loading && <Loader />}
            <div className="flex justify-center items-center"></div>
            <StepShell>
                <StepHeading>
                    {managerDetailsState.data?.recipientName && (
                        <>Hi {managerDetailsState.data?.recipientName}, </>
                    )}{" "}
                    We are delighted about your decision to join the team!!
                </StepHeading>
                <p className="mt-4 text-center text-sm text-slate-500 sm:mt-5 sm:text-base">
                    Please take a few minutes to provide the information we'll need to
                    ensure a smooth onboarding process
                </p>
                <h4 className="mt-10 text-center text-lg font-semibold text-[#0a1445] sm:mt-14 sm:text-xl">
                    Let's start with the {isRizeTeam ? `${BDS_TEAM_LEAD_TITLE}'s` : "Branch Manager's"} Details
                </h4>
            </StepShell>
            <div className="mx-auto w-full max-w-2xl">
                <form>
                    <div className="pb-12 space-y-12">
                        <div className="mt-10 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                                <FloatingLabelInput
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={managerDetailsState.data.firstName}
                                    error={managerDetailsState.formErrors.firstName}
                                    name="firstName"
                                    id="first_name"
                                    label="First Name"
                                    required={true}
                                />
                            </div>
                            <div className="sm:col-span-3">
                                <FloatingLabelInput
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={managerDetailsState.data.lastName}
                                    error={managerDetailsState.formErrors.lastName}
                                    name="lastName"
                                    id="last_name"
                                    label="Last Name"
                                    required={true}
                                />
                            </div>
                        </div>
                        <div className="mt-10 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-6">
                                <FloatingLabelSelect
                                    handleChange={handleChange}
                                    value={managerDetailsState.data.title}
                                    error={managerDetailsState.formErrors.title}
                                    name="title"
                                    id="title"
                                    label="Title"
                                    options={titleOptions}
                                    required={true}
                                />
                            </div>
                        </div>
                        <div className="mt-10 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                                <FloatingLabelInput
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={managerDetailsState.data.email}
                                    error={managerDetailsState.formErrors.email}
                                    name="email"
                                    id="email"
                                    type="email"
                                    label="Email"
                                    required={true}
                                />
                            </div>
                            <div className="sm:col-span-3">
                                <FloatingLabelInput
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={managerDetailsState.data.cellNumber}
                                    error={managerDetailsState.formErrors.cellNumber}
                                    name="cellNumber"
                                    id="cell_number"
                                    type="tel"
                                    label="Cell Number"
                                    required={true}
                                />
                            </div>
                        </div>
                        <div className="mt-10 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                                <FloatingLabelInput
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={managerDetailsState.data.officeNumber || ""}
                                    error={managerDetailsState.formErrors.officeNumber}
                                    name="officeNumber"
                                    id="office_number"
                                    type="tel"
                                    label="Office Number"
                                />
                            </div>
                            {/* NMLS # is not asked for a Business Development Team Lead */}
                            {!isRizeTeam && (
                                <div className="sm:col-span-3">
                                    <FloatingLabelInput
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        value={managerDetailsState.data.nmls || ""}
                                        error={managerDetailsState.formErrors.nmls}
                                        name="nmls"
                                        id="nmls"
                                        type="text"
                                        label="NMLS #"
                                        required={managerDetailsState.data.isOriginatingLoans}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mt-10 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                            {/* <div className="sm:col-span-3">
                                <FloatingLabelInput 
                                        handleChange={handleChange} 
                                        handleBlur={handleBlur}
                                        value={managerDetailsState.data.state || ''} 
                                        error={managerDetailsState.formErrors.state}
                                        name="state" 
                                        id="state" 
                                        type="text" 
                                        label="State" 
                                        required
                                    />
                            </div> */}
                            <div className="sm:col-span-3">
                                <FloatingLabelSelect
                                    handleChange={handleChange}
                                    value={managerDetailsState.data.state || ''}
                                    error={managerDetailsState.formErrors.state}
                                    name="state"
                                    id="state"
                                    label="State"
                                    options={statesList}
                                    required
                                />
                            </div>
                            <div className="sm:col-span-3">
                                <FloatingLabelInput
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={managerDetailsState.data.city || ''}
                                    error={managerDetailsState.formErrors.city}
                                    name="city"
                                    id="city"
                                    type="text"
                                    label="City"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default ManagerDetails;